interface BadgeProps {
  children: React.ReactNode;
  color?: 'gray' | 'blue' | 'yellow' | 'green' | 'green-dark' | 'orange' | 'red';
  className?: string;
}

const colorClasses: Record<NonNullable<BadgeProps['color']>, string> = {
  gray: 'bg-gray-100 text-gray-600',
  blue: 'bg-blue-50 text-brand-blue',
  yellow: 'bg-yellow-50 text-yellow-700',
  green: 'bg-green-50 text-green-600',
  'green-dark': 'bg-green-100 text-green-800',
  orange: 'bg-orange-50 text-orange-600',
  red: 'bg-red-50 text-red-600',
};

export function Badge({ children, color = 'gray', className = '' }: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${colorClasses[color]} ${className}`}
    >
      {children}
    </span>
  );
}

// ── StatusBadge — Maps project/entity states to colors + icons ────
interface StatusConfig {
  color: BadgeProps['color'];
  icon: string;
  label: string;
}

const PROJECT_STATUS_MAP: Record<string, StatusConfig> = {
  contacto_inicial: { color: 'gray', icon: '📋', label: 'Contacto Inicial' },
  datos_listos: { color: 'gray', icon: '📋', label: 'Contacto Inicial' },
  proforma_generada: { color: 'blue', icon: '📄', label: 'Proforma Generada' },
  proforma_en_revision: { color: 'yellow', icon: '🔄', label: 'En Revisión' },
  proforma_aprobada: { color: 'green', icon: '✅', label: 'Aprobada' },
  anticipo_registrado: { color: 'green', icon: '💰', label: 'Anticipo Registrado' },
  en_ejecucion: { color: 'green-dark', icon: '🔨', label: 'En Ejecución' },
  pre_liquidacion: { color: 'orange', icon: '📊', label: 'Pre-Liquidación' },
  finalizado: { color: 'gray', icon: '🏁', label: 'Finalizado' },
};

const PROFORMA_STATUS_MAP: Record<string, StatusConfig> = {
  borrador: { color: 'gray', icon: '📝', label: 'Borrador' },
  enviada: { color: 'yellow', icon: '📤', label: 'Enviada' },
  enviado_al_cliente: { color: 'blue', icon: '📨', label: 'Enviado al cliente' },
  aprobada: { color: 'green', icon: '✅', label: 'Aprobada' },
  rechazada: { color: 'red', icon: '❌', label: 'Rechazada' },
};

const PLANILLA_STATUS_MAP: Record<string, StatusConfig> = {
  borrador: { color: 'gray', icon: '📝', label: 'Borrador' },
  pendiente_compras: { color: 'yellow', icon: '🛒', label: 'Pendiente Compras' },
  pendiente_administrativo: { color: 'yellow', icon: '📋', label: 'Pendiente Admin' },
  correccion_solicitada: { color: 'orange', icon: '🔄', label: 'Corrección Solicitada' },
  revisada: { color: 'blue', icon: '👁️', label: 'Revisada' },
  aprobada: { color: 'green', icon: '✅', label: 'Aprobada' },
  pagada: { color: 'green-dark', icon: '💵', label: 'Pagada' },
};

type EntityType = 'project' | 'proforma' | 'planilla';

const STATUS_MAPS: Record<EntityType, Record<string, StatusConfig>> = {
  project: PROJECT_STATUS_MAP,
  proforma: PROFORMA_STATUS_MAP,
  planilla: PLANILLA_STATUS_MAP,
};

interface StatusBadgeProps {
  entity: EntityType;
  status: string;
  className?: string;
}

export function StatusBadge({ entity, status, className = '' }: StatusBadgeProps) {
  const config = STATUS_MAPS[entity]?.[status] ?? {
    color: 'gray' as const,
    icon: '❓',
    label: status,
  };

  return (
    <Badge color={config.color} className={className}>
      <span>{config.icon}</span>
      {config.label}
    </Badge>
  );
}
